import React from "react";
import { createPortal } from "react-dom";
import {
  X,
  AlertTriangle,
  LogOut,
  Trash2,
  CheckCircle,
  Info,
} from "lucide-react";

export type ConfirmationType =
  | "logout"
  | "delete"
  | "warning"
  | "info"
  | "success";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  type?: ConfirmationType;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
}

const typeConfig = {
  logout: {
    icon: LogOut,
    iconBg: "bg-orange-100",
    iconColor: "text-orange-600",
    confirmBg: "bg-orange-600 hover:bg-orange-700",
    defaultTitle: "Logout",
    defaultMessage:
      "Are you sure you want to logout? You'll need to login again to access your account.",
    defaultConfirm: "Logout",
  },
  delete: {
    icon: Trash2,
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    confirmBg: "bg-red-600 hover:bg-red-700",
    defaultTitle: "Delete",
    defaultMessage:
      "Are you sure you want to delete this? This action cannot be undone.",
    defaultConfirm: "Delete",
  },
  warning: {
    icon: AlertTriangle,
    iconBg: "bg-yellow-100",
    iconColor: "text-yellow-600",
    confirmBg: "bg-yellow-600 hover:bg-yellow-700",
    defaultTitle: "Warning",
    defaultMessage: "Are you sure you want to proceed?",
    defaultConfirm: "Continue",
  },
  info: {
    icon: Info,
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    confirmBg: "bg-blue-600 hover:bg-blue-700",
    defaultTitle: "Confirm",
    defaultMessage: "Do you want to proceed with this action?",
    defaultConfirm: "Confirm",
  },
  success: {
    icon: CheckCircle,
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
    confirmBg: "bg-green-600 hover:bg-green-700",
    defaultTitle: "Success",
    defaultMessage: "Action completed successfully.",
    defaultConfirm: "OK",
  },
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  type = "warning",
  title,
  message,
  confirmText,
  cancelText = "Cancel",
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const config = typeConfig[type];
  const IconComponent = config.icon;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  // Use portal to render modal at document body level
  // This ensures it's above all other elements regardless of stacking context
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-sm transform transition-all animate-in fade-in zoom-in-95 duration-200 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          title="Close"
          onClick={onClose}
          disabled={isLoading}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 z-10"
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className="p-6 text-center">
          {/* Icon */}
          <div
            className={`w-14 h-14 ${config.iconBg} rounded-full flex items-center justify-center mx-auto mb-4`}
          >
            <IconComponent className={config.iconColor} size={28} />
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {title || config.defaultTitle}
          </h3>

          {/* Message */}
          <p className="text-sm text-gray-600 mb-6">
            {message || config.defaultMessage}
          </p>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center ${config.confirmBg}`}
            >
              {isLoading ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              ) : (
                confirmText || config.defaultConfirm
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmationModal;
